import { logSchemaFallback } from "_shared/_utilities/log-schema-failure";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import { z } from "zod";

/**
 * The onboarding status board, as the API sends it.
 *
 * Unlike most of `legal-entities`, this is a read model rather than a domain concept: nothing here
 * is constructed in the browser, edited, or sent back, so it stays a parsed shape with no `create`
 * and no `toJSON` — the same treatment `OnboardingWorkTemplate` gets in the sibling agreements
 * service, and for the same reason.
 */

/** The step statuses this bundle knows how to render. Mirrors `VendorRolloutBoardStepStatus`. */
export const VENDOR_ROLLOUT_STEP_STATUSES = ["NotApplicable", "NotTracked", "Pending", "Completed"] as const;

/**
 * What a status this bundle does not recognize becomes.
 *
 * Not one of the four: each of those is a claim about the firm — "this will never happen for them",
 * "nobody records this yet", "we are waiting", "it is done" — and printing any of them for a value
 * we cannot read would be inventing a fact about someone's roll-out. The board is the page a client
 * reads to decide whether to chase a firm, so a wrong cell is worse than an empty one.
 */
export const UNRECOGNIZED_STEP_STATUS = "Unrecognized";

export type VendorRolloutStepStatus =
  | (typeof VENDOR_ROLLOUT_STEP_STATUSES)[number]
  | typeof UNRECOGNIZED_STEP_STATUS;

/**
 * A status name the API has started sending that this bundle predates.
 *
 * The frontend ships to CloudFront and the API to ECS, so a deploy is never atomic and users hold
 * cached bundles besides (see `logSchemaFallback`). A closed enum would fail the whole board over
 * one cell, which costs the client every other firm's progress as well.
 */
const degradeUnrecognizedStatus = ({ value }: { value: unknown }): VendorRolloutStepStatus => {
  logSchemaFallback({
    schemaName: "VendorRolloutBoardSchema",
    field: "stages[].status",
    value,
    fallback: UNRECOGNIZED_STEP_STATUS,
  });
  return UNRECOGNIZED_STEP_STATUS;
};

/**
 * The four the API sends plus the one only this bundle can produce, so `.catch` has somewhere to
 * land. A payload that literally said "Unrecognized" would parse, which costs nothing: the API has
 * no such value to send.
 */
const VendorRolloutStepStatusZ = z.enum([...VENDOR_ROLLOUT_STEP_STATUSES, UNRECOGNIZED_STEP_STATUS]);

/**
 * One step, and why it is stuck when it is.
 *
 * `stoppedReason` arrives on a step that is still `Pending`, never as a status of its own. The step
 * really has not happened — the letter that reports it never reached the firm — so the honest
 * reading is "pending, and here is why it will stay that way". It also means a bundle that predates
 * this field still prints "Pending" rather than an empty cell, which is what a new status would have
 * cost the roll-out that most needs explaining.
 */
const VendorRolloutBoardStepJSONZ = z.object({
  status: VendorRolloutStepStatusZ.catch(degradeUnrecognizedStatus),
  occurredDate: z.iso.datetime({ offset: true, local: true }).nullish(),
  stoppedReason: z.string().nullish(),
});

/**
 * One stage, with the two steps the operations article's table names.
 *
 * `name` is deliberately a plain string rather than an enum. The board renders whatever stages the
 * API sends, in the order it sends them, so a fifth stage — first invoice becoming real, most
 * likely — appears without waiting for a frontend deploy, labelled from its name.
 */
const VendorRolloutBoardStageJSONZ = z.object({
  name: z.string().min(1),
  started: VendorRolloutBoardStepJSONZ,
  finished: VendorRolloutBoardStepJSONZ,
  notTrackedReason: z.string().nullish(),
  notTrackedArticleSlug: z.string().nullish(),
});

/**
 * One firm's roll-out.
 *
 * The ids are `z.guid()` rather than `z.uuid()`. `z.uuid()` also checks the RFC 9562 version and
 * variant bits, which a .NET `Guid` never promises: any 128-bit value is a valid one, and a row
 * inserted with a hand-written id such as `11111111-1111-1111-1111-111111111111` failed the whole
 * board — every firm's progress lost over an id the page only uses as a key.
 */
const VendorRolloutBoardRowJSONZ = z.object({
  rolloutId: z.guid(),
  clientLegalEntityId: z.guid(),
  clientName: z.string(),
  vendorLegalEntityId: z.guid().nullish(),
  firmName: z.string(),
  teamLeaderName: z.string(),
  teamLeaderEmail: z.string(),
  teamLeaderTitle: z.string(),
  startingState: z.string(),
  stage: z.string(),
  stages: z.array(VendorRolloutBoardStageJSONZ),
  createdDate: z.iso.datetime({ offset: true, local: true }),
  closedDate: z.iso.datetime({ offset: true, local: true }).nullish(),
  closureReason: z.string().nullish(),
  /**
   * When the roll-out last moved, which the board can be ordered by (#5449). Optional because the
   * API and this bundle deploy separately, and an answer from an API that predates it is still a
   * board worth drawing.
   */
  lastActivityDate: z.iso.datetime({ offset: true, local: true }).nullish(),
});

const VendorRolloutBoardJSONZ = z.object({
  assembledAt: z.iso.datetime({ offset: true, local: true }),
  rows: z.array(VendorRolloutBoardRowJSONZ),
});

export type VendorRolloutBoardStepJSON = z.infer<typeof VendorRolloutBoardStepJSONZ>;
export type VendorRolloutBoardStageJSON = z.infer<typeof VendorRolloutBoardStageJSONZ>;
export type VendorRolloutBoardRowJSON = z.infer<typeof VendorRolloutBoardRowJSONZ>;
export type VendorRolloutBoardJSON = z.infer<typeof VendorRolloutBoardJSONZ>;

export default class VendorRolloutBoardSchema {
  public static readonly VendorRolloutBoardStepJSONZ = VendorRolloutBoardStepJSONZ;
  public static readonly VendorRolloutBoardStageJSONZ = VendorRolloutBoardStageJSONZ;
  public static readonly VendorRolloutBoardRowJSONZ = VendorRolloutBoardRowJSONZ;
  public static readonly VendorRolloutBoardJSONZ = VendorRolloutBoardJSONZ;

  public static validateJSON(json: unknown): VendorRolloutBoardJSON {
    return parseOrThrow(VendorRolloutBoardJSONZ, json, {
      logError: true,
      schemaName: "VendorRolloutBoardSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValidJSON(json: unknown): json is VendorRolloutBoardJSON {
    return VendorRolloutBoardJSONZ.safeParse(json).success;
  }
}
